import * as React from "react";

import { enUS as enDateFns } from 'date-fns/locale';
import { enUS as enMaterial } from "@mui/material/locale";
import { hr as hrDateFns } from 'date-fns/locale';
import { hrHR as hrMaterial } from "@mui/material/locale";
import {dynamicActivateLocale} from "./locales";
import {changeLocale, initialState, LocaleState} from "./localeSlice";
import {createTheme} from "@mui/material/styles";
import {theme} from "./theme";
import {useDispatch, useSelector} from "react-redux";
import {useMemo} from "react";
import {Localization} from "@mui/material/locale";
import {useAppDispatch, useAppSelector} from "../../../../app/store.tsx";
import { useLocalStorage } from 'react-use';

export type LocalizationMap = { [id: string]: Localization };


declare module "@mui/material/styles" {
    interface Palette {
        /**
         * Surfaces specific to the AI chat.
         *
         * The chat needs a handful of near-neutral backgrounds that are neither
         * "paper" nor "default" in the ordinary sense -- a message bubble and a
         * code block are not the same surface. They used to be picked inline
         * with `palette.mode === 'dark' ? grey.x : grey.y`, repeated in four
         * places across three files, which is how a dark-mode chat ended up with
         * a white panel inside it.
         *
         * Naming them by role rather than by colour means the scheme decides the
         * value, and a component can only get this right by asking for what it
         * wants.
         */
        chat?: {
            /** The scrolling thread behind the bubbles. */
            canvas: string;
            /** An assistant message bubble. */
            bubble: string;
            /** A fenced code block inside a reply. */
            codeBlock: string;
            /** Inline `code` spans inside a reply. */
            inlineCode: string;
        };
    }
}

const dateFnsLocales = {
    
    enUS: enDateFns,
    hr: hrDateFns,
}

const muiLocales: LocalizationMap = {
    
    en: enMaterial,
    hr: hrMaterial,
}

export const LOCALE_TOKEN_KEY = 'locale';

export function useLocale() {
    const [prefsToken, setPrefsToken] = useLocalStorage(LOCALE_TOKEN_KEY)
    const dispatch = useAppDispatch()
    let localeState: LocaleState = useAppSelector(state => state.locale)

    React.useEffect(() => {
        if (!localeState.updated) {
            try {
                const preferences = prefsToken && JSON.parse(prefsToken) || {};
                if (preferences) {
                    localeState = preferences;
                    dispatch(changeLocale(localeState))
                }
            } catch (error) {
                console.error('Failed to read preferences: ', error);
            }
        }
    }, [localeState.updated])

    React.useEffect(() => {
        if (localeState.updated) {
            setPrefsToken(JSON.stringify(localeState))
        }
    }, [localeState])

    const selectLocaleKey = React.useCallback((localeKey: string) => {
        dispatch(changeLocale({localeKey}))
    }, [dispatch])

    React.useEffect(() => {
        if (localeState.localeKey) {
            dynamicActivateLocale(localeState.localeKey)
        }
    }, [localeState.localeKey])

    return {
        localeKey: localeState.localeKey,
        selectLocaleKey,
        dateFnsLocale: dateFnsLocales[localeState.localeKey],
    }
}

export function useLocaleTheme(
    defaultLocaleKey: string | undefined = undefined
) {
    const dispatch = useDispatch();
    const { localeKey: localeKeyState } = useSelector((state: { 'locale': any }) => state.locale);

    const localeKey = localeKeyState ?? defaultLocaleKey;

    const themeWithLocale = useMemo(
        () => {
            if (localeKey) {
                // The comparison was `localeKey !== localeKey`, which is never
                // true, so a language chosen at runtime never reached the
                // Material locale: the dates and numbers kept the old
                // formatting until the page was reloaded.
                if (localeKey !== localeKeyState) {
                    dispatch(changeLocale({localeKey}))
                }
                return createTheme(theme, muiLocales[localeKey])
            } else {
                return theme
            }
        },
        [localeKey, localeKeyState, theme, dispatch]
    );

    return {
        themeWithLocale,
    };
}
